import type { ImportResult } from '@panelvault/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Download, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../components/ui/button';
import { Panel } from '../components/ui/panel';
import { BackupPanel } from '../features/backup/BackupPanel';
import { SourcesPanel } from '../features/sources/SourcesPanel';
import { ApiError, uploadFile } from '../lib/api';

export function SettingsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);

  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleImport(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setError(null);
    setResult(null);

    try {
      const response = await uploadFile<ImportResult>('/import', file);
      setResult(response);
      await queryClient.invalidateQueries();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t('form.saveFailed'));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <header>
        <h1 className="text-3xl text-foreground">{t('nav.settings')}</h1>
      </header>

      <BackupPanel />

      <Panel className="flex flex-col gap-4 p-5">
        <div>
          <h2 className="text-xl text-foreground">{t('settings.exportTitle')}</h2>
          <p className="mt-1 text-sm text-muted">{t('settings.exportBody')}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <a
            href="/api/export.json"
            className="inline-flex h-11 items-center gap-2 rounded-lg bg-accent px-4 text-sm font-medium text-accent-foreground hover:bg-accent-strong"
          >
            <Download className="size-4" />
            {t('settings.exportJson')}
          </a>
          <a
            href="/api/export.csv"
            className="inline-flex h-11 items-center gap-2 rounded-lg bg-surface-raised px-4 text-sm font-medium text-foreground hover:bg-border"
          >
            <Download className="size-4" />
            {t('settings.exportCsv')}
          </a>
        </div>
      </Panel>

      <Panel className="flex flex-col gap-4 p-5">
        <div>
          <h2 className="text-xl text-foreground">{t('settings.importTitle')}</h2>
          <p className="mt-1 text-sm text-muted">{t('settings.importBody')}</p>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept=".json,.csv,application/json,text/csv"
          className="hidden"
          onChange={(event) => void handleImport(event.target.files?.[0])}
        />

        <Button variant="surface" disabled={busy} onClick={() => inputRef.current?.click()}>
          <Upload className="size-4" />
          {busy ? t('settings.importing') : t('settings.chooseFile')}
        </Button>

        {result ? (
          <div className="rounded-lg border bg-surface-raised p-4 text-sm">
            <p className="text-foreground">{t('settings.importDone')}</p>
            <ul className="mt-2 space-y-1 text-muted">
              <li>{t('settings.importSeries', { count: result.series })}</li>
              <li>{t('settings.importIssues', { count: result.issues })}</li>
              <li>{t('settings.importCopies', { count: result.collectionItems })}</li>
              <li>{t('settings.importSkipped', { count: result.skipped })}</li>
            </ul>
            {result.errors.length > 0 ? (
              <ul className="mt-3 space-y-1 text-danger">
                {result.errors.slice(0, 10).map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}
      </Panel>

      <SourcesPanel />
    </div>
  );
}
